import { Box, Typography } from '@mui/material';
import { usePostHog } from 'posthog-js/react';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import helpIcon from '../../assets/icons/helpCenter.svg';
import { RETELL } from '../../constant/retell.constant';
import { useAuth } from '../../hooks/useAuth';
import { useDomain } from '../../hooks/useDomain';
import { getChargerStatus } from '../../util/charger-status-utils';
import { useRetellAgentConfig } from '../../services/retell-config.service';
import { ROUTES } from '../../types/routes.enum';
import { CallbackConfirmationState } from '../../types/callback-confirmation-state.interface';
import { RetellDynamicVariables } from '../../types/retell.types';
import { SupportButton } from '../ui-components/button/support-button.component';
import SupportIcon from '../../assets/icons/support.svg';
import { authStyles } from './authStyles';
import { TestCompanyStatus } from '../charger/dto/charger.dto';

interface HaveQuestionsProps {
  chargerDetail: any; // Required
  createZendeskTicket?: () => any; // Optional function to create a zendesk ticket for charger support
  style?: any;
}

const HaveQuestions = ({ chargerDetail, createZendeskTicket, style = {} }: HaveQuestionsProps) => {
  const { i18n } = useTranslation();
  const classes = authStyles();
  const posthog = usePostHog();
  const retellPosthogFeatureFlag = posthog && posthog.isFeatureEnabled('retell-callback-support');
  const auth = useAuth();
  const navigate = useNavigate();
  const { domain } = useDomain();

  const chargerBelongsToTestCompany = !(
    chargerDetail.testCompany === TestCompanyStatus.NOT_FROM_TEST_COMPANY ||
    !chargerDetail.testCompany
  );

  const callSupport = useCallback(() => {
    if (createZendeskTicket) createZendeskTicket();
    window.location.href = `tel:${chargerDetail.contactInfo}`;
  }, [chargerDetail.contactInfo, createZendeskTicket]);

  // Normalize language code (e.g., 'en-US' -> 'en', 'fr-CA' -> 'fr')
  const normalizedLanguage = (i18n.language || 'en').split('-')[0].toLowerCase();

  // Get retell agent config based on charger's contact info (phone number) and language
  const retellAgentConfig = useRetellAgentConfig(chargerDetail.contactInfo, normalizedLanguage);

  const retellDynamicVariables: RetellDynamicVariables = useMemo(() => {
    const userFirstName = (auth.user?.attributes?.given_name || '').trim();
    const userLastName = (auth.user?.attributes?.family_name || '').trim();
    const userName = (userFirstName + ' ' + userLastName).trim();
    return {
      timestamp: String(new Date().toISOString()),
      user_email: String(auth.user?.attributes?.email || ''),
      user_phone: String(auth.user?.attributes?.phone_number || ''),
      user_first_name: String(userFirstName),
      user_last_name: String(userLastName),
      user_name: String(userName),
      charger_id: String(chargerDetail.chargerId),
      charger_name: String(chargerDetail.name),
      charger_type: String(chargerDetail.type),
      charger_model: String(chargerDetail.model),
      charger_status: String(getChargerStatus(chargerDetail)),
      charger_in_maintenance: String(chargerDetail.maintenanceFlag),
      charger_requires_payment: String(chargerDetail.requirePaymentMethod),
      charger_accessible: String(chargerDetail.userHasAccess),
      charger_owner: String(chargerDetail.owner.id),
      location_id: String(chargerDetail.location?.locationId),
      location_name: String(chargerDetail.location?.name),
      location_latitude: String(chargerDetail.location?.latitude),
      location_longitude: String(chargerDetail.location?.longitude),
      location_iso_country_code: String(chargerDetail.location?.isoCountryCode),
      location_state_or_region: String(chargerDetail.location?.stateOrRegion),
      location_city: String(chargerDetail.location?.city),
      location_street_address: String(chargerDetail.location?.streetAddress),
      location_zip_or_postal_code: String(chargerDetail.location?.zipOrPostalCode),
      location_time_zone: String(chargerDetail.location?.timeZone),
    };
  }, [chargerDetail, auth.user]);

  // This method assumes that the proper checks have been taken beforehand to ensure:
  // - the user is logged in
  // - the retell feature flag is enabled
  // - a retell agent config is available
  const handleRetellCallbackClick = () => {
    // Get user information if logged in
    const userPhoneNumber = auth.user?.attributes?.phone_number || '';
    const givenName = (auth.user?.attributes?.given_name || '').trim();
    const familyName = (auth.user?.attributes?.family_name || '').trim();
    const userName = [givenName, familyName].join(' ').trim();

    // Navigate to the callback confirmation page with the necessary data
    const callbackState: CallbackConfirmationState = {
      publicKey: RETELL.publicKey,
      agentId: retellAgentConfig.agentId,
      chargerId: chargerDetail.chargerId,
      chargerName: chargerDetail.name,
      chargerSupportPhoneNumber: chargerDetail.contactInfo,
      fromPhoneNumber: retellAgentConfig.phoneNumber,
      initialPhoneNumber: userPhoneNumber,
      customerName: userName,
      countries: retellAgentConfig.countries.join(','),
      dynamicVariables: retellDynamicVariables,
    };

    navigate(`/app/${domain}/${ROUTES.CALLBACK_CONFIRMATION}`, {
      state: callbackState,
    });
  };

  const handleContactSupportClick = () => {
    if (retellPosthogFeatureFlag && auth.isLoggedIn && retellAgentConfig) {
      handleRetellCallbackClick();
    } else {
      callSupport();
    }
  };

  const helpCenterUrl = (() => {
    switch (chargerDetail.whiteLabellingId ? chargerDetail.whiteLabellingId.toUpperCase() : '') {
      case 'CHARGELAB':
        return 'https://chargelab.zendesk.com/hc/en-us/sections/25563504258843-Charging-Basics';
      case 'EATON':
        return 'https://eatoncharging.zendesk.com/hc/en-ca/sections/27529579005339-How-to-start-a-charging-session';
      case 'FILGO':
        if (normalizedLanguage === 'fr') {
          return 'https://filgo.zendesk.com/hc/fr-ca';
        } else {
          return 'https://filgo.zendesk.com/hc/en-ca';
        }
      default:
        return null;
    }
  })();

  if (chargerBelongsToTestCompany) {
    return null;
  }
  return (
    <div style={{ width: '100%' }}>
      <Box className={classes.footerBox} style={{ marginBottom: '16px', gap: '8px', ...style }}>
        <Typography
          variant='h3'
          style={{
            fontFamily: 'Inter',
            fontSize: '16px',
            fontWeight: '600',
            lineHeight: '24px',
            textAlign: 'center',
            color: '#4E5968',
            marginBottom: '16px',
          }}
        >
          {i18n.t('still_have_questions')}
        </Typography>
        <SupportButton
          onClick={handleContactSupportClick}
          startIcon={<img src={SupportIcon} alt='Support' />}
          fullWidth
          buttonText={i18n.t('contact_support')}
          tabIndex={0}
          color='#FFFFFF'
        />
        {helpCenterUrl && (
          <SupportButton
            onClick={() => window.open(helpCenterUrl)}
            startIcon={<img src={helpIcon} alt='Help Center' />}
            fullWidth
            buttonText={i18n.t('visit_help_center')}
            tabIndex={1}
          />
        )}
      </Box>
    </div>
  );
};

export default HaveQuestions;
